import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import FaqJsonLd from "@/components/FaqJsonLd";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CardRenderer from "@/components/cartes/CardRenderer";
import {
  Sparkles, QrCode, Share2, Contact, Printer, BarChart3, Zap, CheckCircle2,
  ArrowRight, Smartphone, Leaf, Clock, Infinity as InfinityIcon, ChevronRight,
} from "lucide-react";

// Vers quelle inscription rediriger selon le type de carte
const REGISTER_ROUTE = {
  pro: "/inscription?role=professional", entreprise: "/inscription?role=professional",
  independant: "/inscription?role=professional", particulier: "/inscription",
  evenement: "/inscription", association: "/associations/inscription",
  club: "/clubs/inscription", cse: "/organismes-collectivites/inscription",
  collectif: "/organismes-collectivites/inscription",
};

const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/66457276-1572-4d20-83d0-d7f4ad854fc6/images/ce0b82362652e9c6970fa8b3e503b4a45ae0ae1b8d8caa1f827ba0909ee0789e.jpeg";

function setMetaTag(attr, key, val) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  let prev = null, created = false;
  if (el) { prev = el.getAttribute("content"); }
  else { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); created = true; }
  el.setAttribute("content", val);
  return () => { if (created) el.remove(); else el.setAttribute("content", prev ?? ""); };
}

function buildCard(tpl, identity, contact, about) {
  return {
    card_type: "pro", template_id: tpl?.id,
    theme: { layout: tpl?.layout, colors: tpl?.colors || {}, fonts: tpl?.fonts || {} },
    identity, about, contact,
    actions: { call: !!contact.phone, email: !!contact.email, whatsapp: false, website: !!contact.website, directions: false },
    socials: [],
  };
}

const DEMO = {
  identity: { display_name: "Camille Durand", org_name: "Studio Durand", role_title: "Photographe", slogan: "Capter l'instant, sublimer votre image" },
  about: "Photographe portraitiste à Chartres. Portraits, événements et reportages d'entreprise.",
  contact: { phone: "0612345678", email: "camille@studio-durand.fr", website: "studio-durand.fr", show_address: false },
};

export default function CarteVisiteLanding() {
  useSeo({
    title: "Carte de visite digitale professionnelle — QR code & vCard | PROXIreduc-28",
    description: "Créez votre carte de visite digitale professionnelle : partage par QR code, vCard et PDF, référencée localement en Eure-et-Loir. Gratuit avec l'offre Fondateurs PROXIreduc-28.",
    canonicalPath: "/carte-de-visite-digitale",
    image: "/og-image.jpg",
  });
  const nav = useNavigate();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const builderRef = useRef(null);

  useEffect(() => {
    document.title = "Ma carte de visite digitale gratuite — PROXIreduc-28";
    const restores = [
      setMetaTag("name", "description", "Créez gratuitement votre carte de visite digitale en 5 minutes : QR code, partage entre 2 téléphones, ajout aux contacts. Sans application."),
      setMetaTag("property", "og:title", "Ma carte de visite digitale — gratuite, prête en 5 minutes"),
      setMetaTag("property", "og:description", "Fini les cartes papier oubliées. Créez votre carte, partagez-la d'un simple geste et ne perdez plus jamais un contact. 100 % gratuit."),
      setMetaTag("property", "og:image", OG_IMAGE),
      setMetaTag("property", "og:type", "website"),
      setMetaTag("property", "og:url", window.location.href),
      setMetaTag("name", "twitter:card", "summary_large_image"),
      setMetaTag("name", "twitter:title", "Ma carte de visite digitale — gratuite"),
      setMetaTag("name", "twitter:description", "Créez et partagez votre carte de visite digitale gratuitement sur PROXIreduc-28."),
      setMetaTag("name", "twitter:image", OG_IMAGE),
    ];
    api.get("/cartes/public-meta").then(({ data }) => setMeta(data)).catch(() => {});
    return () => { restores.forEach((r) => r && r()); };
  }, []);

  const heroTpl = useMemo(() => meta?.templates?.find((t) => t.id === "premium-bleu-nuit") || meta?.templates?.[0], [meta]);
  const scrollToBuilder = () => builderRef.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-[#0B1220] text-white overflow-hidden">
      <FaqJsonLd faq={[
        { q: "Qu'est-ce qu'une carte de visite digitale ?", a: "C'est une carte professionnelle en ligne, partageable par QR code, vCard et PDF, qui regroupe vos coordonnées, liens et réseaux sociaux et se met à jour en temps réel." },
        { q: "La carte de visite digitale est-elle gratuite ?", a: "Oui, elle est incluse gratuitement avec l'offre Fondateurs de PROXIreduc-28 pour les professionnels de l'Eure-et-Loir." },
        { q: "Comment partager ma carte de visite digitale ?", a: "Via un QR code à présenter, un lien direct, un fichier vCard à enregistrer dans les contacts ou un PDF, sans application à installer." },
      ]} />
      {/* ================= HERO ================= */}
      <section className="relative">
        <div className="absolute inset-0 bg-[radial-gradient(60%_60%_at_20%_10%,rgba(224,86,56,.25),transparent),radial-gradient(50%_50%_at_90%_20%,rgba(59,130,246,.22),transparent)]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest bg-white/10 border border-white/15 rounded-full px-3 py-1.5" data-testid="hero-badge">
              <Sparkles size={13} className="text-[#E05638]" /> Nouveau · 100 % gratuit
            </span>
            <h1 className="mt-5 font-heading font-extrabold leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
              Votre carte de visite<br /><span className="text-[#E05638]">digitale</span>, prête en 5 minutes.
            </h1>
            <p className="mt-5 text-lg text-slate-300 max-w-xl">
              Fini les cartes papier oubliées au fond d'une poche. Créez une carte moderne,
              partagez-la d'un simple geste entre deux téléphones, et ne perdez plus jamais un contact.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={scrollToBuilder} data-testid="hero-cta" className="bg-[#E05638] hover:bg-[#c94a2f] text-white text-base h-12 px-7 rounded-xl font-bold shadow-lg shadow-orange-900/30">
                Créer ma carte gratuitement <ArrowRight size={18} className="ml-1" />
              </Button>
              <a href="/carte/exemple" target="_blank" rel="noreferrer">
                <Button variant="outline" className="h-12 px-6 rounded-xl border-white/20 bg-white/5 text-white hover:bg-white/10" data-testid="hero-demo">
                  Voir un exemple
                </Button>
              </a>
            </div>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              {["Sans application", "Sans engagement", "Partage en 1 geste"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 size={15} className="text-emerald-400" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="relative flex justify-center">
            <div className="absolute -inset-6 bg-[#E05638]/10 blur-3xl rounded-full" />
            {heroTpl && (
              <div className="relative w-[280px] rounded-[2rem] border-[9px] border-black/70 overflow-hidden shadow-2xl bg-white max-h-[560px] overflow-y-auto rotate-[2deg] hover:rotate-0 transition-transform duration-500" data-testid="hero-preview">
                <CardRenderer card={buildCard(heroTpl, DEMO.identity, DEMO.contact, DEMO.about)} preview />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ================= DOULEURS / BÉNÉFICES ================= */}
      <section className="bg-white text-slate-900 py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <p className="text-center text-[#E05638] font-bold uppercase tracking-widest text-xs">Pourquoi passer au digital</p>
          <h2 className="text-center font-heading text-3xl sm:text-4xl font-extrabold mt-2">88 % des cartes papier sont jetées dans la semaine.</h2>
          <p className="text-center text-slate-500 mt-3 max-w-2xl mx-auto">Votre carte digitale, elle, reste dans le téléphone de vos contacts — toujours à jour, toujours accessible, impossible à perdre.</p>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {[
              [Smartphone, "Toujours sur vous", "Votre carte vit dans votre téléphone. Partagez-la n'importe où, même sans l'avoir imprimée."],
              [InfinityIcon, "Jamais obsolète", "Changé de numéro ou de logo ? Vous modifiez, et tous vos contacts ont la version à jour."],
              [Leaf, "Écologique & économique", "Zéro impression, zéro gaspillage. Une seule carte, partageable à l'infini, gratuitement."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="w-11 h-11 rounded-xl bg-orange-50 text-[#E05638] grid place-items-center mb-3"><Icon size={22} /></div>
                <h3 className="font-bold text-lg">{t}</h3>
                <p className="text-slate-500 text-sm mt-1.5">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= BUILDER ================= */}
      <section ref={builderRef} className="bg-[#FAF8F5] text-slate-900 py-16" id="creer">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <p className="text-center text-[#E05638] font-bold uppercase tracking-widest text-xs">Essayez maintenant</p>
          <h2 className="text-center font-heading text-3xl sm:text-4xl font-extrabold mt-2">Composez votre carte en direct</h2>
          <p className="text-center text-slate-500 mt-3">Choisissez un style, personnalisez, et voyez le résultat instantanément. C'est gratuit.</p>
          {meta ? <Builder meta={meta} user={user} nav={nav} /> : <div className="text-center text-slate-400 py-16">Chargement du studio…</div>}
        </div>
      </section>

      {/* ================= FONCTIONNALITÉS ================= */}
      <section className="bg-[#0B1220] py-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <h2 className="text-center font-heading text-3xl sm:text-4xl font-extrabold">Tout ce dont vous avez besoin, inclus.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {[
              [QrCode, "QR code dynamique", "Un QR unique à scanner pour ouvrir votre carte, même sur une vitrine ou un flyer."],
              [Share2, "Partage entre 2 téléphones", "Un geste suffit : AirDrop, Quick Share, WhatsApp, SMS… sans aucune application."],
              [Contact, "Ajout aux contacts", "Vos coordonnées enregistrées en un clic dans le répertoire de votre interlocuteur."],
              [BarChart3, "Statistiques réelles", "Vues, clics, scans… mesurez l'impact de votre carte en temps réel."],
              [Printer, "Version imprimable", "Besoin de papier aussi ? Exportez un PDF prêt pour l'imprimeur."],
              [Zap, "Assistant IA inclus", "L'IA rédige votre présentation et choisit vos couleurs à votre place."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-2xl bg-white/5 border border-white/10 p-5">
                <Icon size={22} className="text-[#E05638]" />
                <h3 className="font-bold mt-3">{t}</h3>
                <p className="text-slate-400 text-sm mt-1">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA FINAL ================= */}
      <section className="bg-gradient-to-br from-[#E05638] to-[#b8381f] py-16 text-center">
        <div className="max-w-2xl mx-auto px-5">
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold">Et si votre prochaine rencontre devenait un contact ?</h2>
          <p className="mt-3 text-white/90">Créez votre carte de visite digitale gratuitement — et faites forte impression dès aujourd'hui.</p>
          <Button onClick={scrollToBuilder} data-testid="final-cta" className="mt-7 bg-white text-[#E05638] hover:bg-slate-100 h-12 px-8 rounded-xl font-bold text-base">
            Je crée ma carte maintenant <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Builder interactif ---------------- */
function Builder({ meta, user, nav }) {
  const [step, setStep] = useState(1);
  const [cardType, setCardType] = useState("");
  const [tpl, setTpl] = useState(null);
  const [identity, setIdentity] = useState({ display_name: "", org_name: "", role_title: "", slogan: "" });
  const [contact, setContact] = useState({ phone: "", email: "", website: "", show_address: false });
  const [about, setAbout] = useState("");
  const [busy, setBusy] = useState(false);

  const templates = useMemo(() => (cardType ? meta.templates.filter((t) => t.card_types.includes(cardType)) : []), [meta, cardType]);
  const previewCard = tpl ? { ...buildCard(tpl, identity, contact, about), card_type: cardType } : null;

  const finish = async () => {
    if (!identity.display_name.trim()) { toast.error("Indiquez au moins votre nom."); return; }
    setBusy(true);
    const draft = { card_type: cardType, template_id: tpl.id, identity, contact, about,
      actions: previewCard.actions };
    try {
      if (user && user !== false) {
        localStorage.removeItem("macarte_draft");
        // déjà connecté : on crée la carte directement
        const { data } = await api.post("/cartes", { card_type: cardType, template_id: tpl.id });
        await api.put(`/cartes/${data.id}`, { identity, contact, about, actions: previewCard.actions });
        toast.success("Carte créée ! Finalisez-la puis publiez.");
        nav(`/ma-carte/${data.id}/editer`);
      } else {
        // visiteur : on garde le brouillon et on envoie vers l'inscription de sa catégorie
        localStorage.setItem("macarte_draft", JSON.stringify(draft));
        toast.success("Dernière étape : créez votre compte gratuit pour publier votre carte.");
        window.scrollTo(0, 0);
        nav(REGISTER_ROUTE[cardType] || "/inscription");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Une erreur est survenue."); setBusy(false); }
  };

  return (
    <div className="mt-8 bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden" data-testid="builder">
      {/* progression */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 text-xs font-semibold text-slate-400">
        {["Type", "Style", "Infos"].map((l, i) => (
          <span key={l} className={`inline-flex items-center gap-1.5 ${step === i + 1 ? "text-[#E05638]" : step > i + 1 ? "text-emerald-600" : ""}`}>
            <span className={`w-5 h-5 rounded-full grid place-items-center text-[11px] ${step >= i + 1 ? "bg-[#E05638] text-white" : "bg-slate-200"}`}>{i + 1}</span>{l}
            {i < 2 && <ChevronRight size={14} className="text-slate-300" />}
          </span>
        ))}
      </div>

      <div className="grid lg:grid-cols-5">
        <div className="lg:col-span-3 p-5 min-h-[380px]">
          {step === 1 && (
            <div data-testid="builder-step-type">
              <h3 className="font-bold mb-3">Je crée une carte pour…</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(meta.card_types).map(([k, v]) => (
                  <button key={k} onClick={() => { setCardType(k); setTpl(null); setStep(2); }} data-testid={`builder-type-${k}`}
                    className={`text-left p-3 rounded-xl border transition-all ${cardType === k ? "border-[#E05638] bg-orange-50" : "border-slate-200 hover:border-[#E05638]"}`}>
                    <span className="font-semibold text-sm text-slate-900">{v.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div data-testid="builder-step-style">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold">Choisissez un style</h3>
                <button onClick={() => setStep(1)} className="text-xs text-slate-400 hover:text-slate-600">← Type</button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[320px] overflow-y-auto pr-1">
                {templates.map((t) => (
                  <button key={t.id} onClick={() => { setTpl(t); setStep(3); }} data-testid={`builder-tpl-${t.id}`}
                    className={`rounded-xl overflow-hidden border-2 ${tpl?.id === t.id ? "border-[#E05638]" : "border-slate-200"}`} title={t.name}>
                    <div className="h-20 flex flex-col">
                      <span className="h-9" style={{ background: `linear-gradient(135deg, ${t.colors?.primary}, ${t.colors?.secondary})` }} />
                      <span className="flex-1 flex flex-col items-center justify-center gap-1" style={{ background: t.colors?.bg }}>
                        <span className="h-1.5 w-10 rounded" style={{ background: t.colors?.title }} />
                        <span className="h-2 w-8 rounded" style={{ background: t.colors?.button }} />
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div data-testid="builder-step-infos" className="space-y-2.5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold">Vos informations</h3>
                <button onClick={() => setStep(2)} className="text-xs text-slate-400 hover:text-slate-600">← Style</button>
              </div>
              <Input placeholder="Nom affiché *" value={identity.display_name} onChange={(e) => setIdentity({ ...identity, display_name: e.target.value })} data-testid="builder-name" />
              <Input placeholder={cardType === "evenement" ? "Nom de l'événement" : "Entreprise / organisation"} value={identity.org_name} onChange={(e) => setIdentity({ ...identity, org_name: e.target.value })} />
              <Input placeholder="Fonction / métier" value={identity.role_title} onChange={(e) => setIdentity({ ...identity, role_title: e.target.value })} />
              <Input placeholder="Slogan (optionnel)" value={identity.slogan} onChange={(e) => setIdentity({ ...identity, slogan: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Téléphone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="builder-phone" />
                <Input placeholder="E-mail" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              </div>
              <Button onClick={finish} disabled={busy} className="w-full bg-[#E05638] hover:bg-[#c94a2f] h-11 rounded-xl font-bold mt-1" data-testid="builder-finish">
                {busy ? "Création…" : (user && user !== false ? "Créer et personnaliser ma carte" : "Créer mon compte & publier ma carte")} <ArrowRight size={17} className="ml-1" />
              </Button>
              <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1"><Clock size={12} /> Gratuit · moins de 5 minutes · modifiable à tout moment</p>
            </div>
          )}
        </div>

        {/* aperçu */}
        <div className="lg:col-span-2 bg-slate-100 p-5 flex items-center justify-center">
          {previewCard ? (
            <div className="w-[240px] rounded-[1.8rem] border-[8px] border-slate-900 overflow-hidden shadow-xl bg-white max-h-[420px] overflow-y-auto" data-testid="builder-preview">
              <CardRenderer card={previewCard} preview />
            </div>
          ) : (
            <div className="text-center text-slate-400 text-sm px-6"><IdPlaceholder /> Votre aperçu apparaîtra ici dès que vous choisissez un style.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function IdPlaceholder() {
  return <div className="w-16 h-16 mx-auto mb-3 rounded-2xl border-2 border-dashed border-slate-300 grid place-items-center"><Sparkles className="text-slate-300" /></div>;
}
